/**
 * 通知プリセットの表示用フォーマット（D-083）。
 *
 * プリセットのDSLは ISO8601 の期間（`-P14D`）や曜日コード（`FRI`）をそのまま持つ。
 * 画面にそのまま出すと事務局には読めないため、日本語に直してから表示する。
 */

// バックエンドの Weekday は小文字コード（mon/tue/...）。以前は大文字キーで定義しており
// 一致せず常に生コードが出てしまっていた（実機確認で発覚 / D-156）
export const WEEKDAY_LABELS: Record<string, string> = {
  mon: '月曜',
  tue: '火曜',
  wed: '水曜',
  thu: '木曜',
  fri: '金曜',
  sat: '土曜',
  sun: '日曜',
};

export const WEEKDAY_OPTIONS = Object.keys(WEEKDAY_LABELS);

/** `-P14D` → `14日前` / `PT2H` → `2時間後` / `PT0S`・未設定 → 空文字 */
export function formatOffset(offset: string | undefined | null): string {
  if (!offset || offset === 'PT0S') return '';
  const match = /^(-)?P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/.exec(offset);
  // 想定外の書式は隠さずそのまま出す（誤った日本語に置き換えて誤解させない）
  if (!match) return offset;
  const [, sign, days, hours, minutes] = match;
  const parts = [
    days ? `${days}日` : '',
    hours ? `${hours}時間` : '',
    minutes ? `${minutes}分` : '',
  ].filter(Boolean);
  if (parts.length === 0) return '';
  return `${parts.join('')}${sign ? '前' : '後'}`;
}

/** ルールの宛先（AudienceKey）の日本語ラベル */
export const AUDIENCE_LABELS: Record<string, string> = {
  participants: '参加者',
  directors: '理事・講師',
  all_students: '受講生全体',
  success: 'サクセス担当',
  office: '事務局',
  all: '全員',
};

/** ルールの起点（TriggerBase）の日本語ラベル */
export const TRIGGER_BASE_LABELS: Record<string, string> = {
  event_start: '開催日時',
  event_end: '終了日時',
  survey_open: 'アンケート公開',
  survey_deadline: 'アンケート期限',
  request_created: '依頼作成',
  monthly_day: '毎月n日（カレンダー基準）',
  weekly: '毎週（カレンダー基準）',
};

/** カレンダー基準の起点（イベント等の日時からの相対指定ではなく、日付・曜日で直接指定する） */
export const CALENDAR_TRIGGER_BASES = new Set(['monthly_day', 'weekly']);

/** 配信条件（condition）の日本語ラベル。実装済みの2種類のみ選べる */
export const CONDITION_LABELS: Record<string, string> = {
  has_unsubmitted: '未提出者がいる場合のみ送る',
  has_weekly_success_records: '今週分の議事録がある場合のみ送る',
};

/** 通知テンプレートの分類（NotificationCategory）の日本語ラベル */
export const TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  event: 'イベント',
  survey: 'アンケート',
  message: 'メッセージ',
  success: 'サクセス',
  score: '採点',
  system: 'システム',
};

/**
 * 差し込み変数の正本（D-290）。
 * 本文に `{{変数名}}` と書く。送信時に置換される。未定義の変数は空文字になる。
 */
export const TEMPLATE_VARIABLE_ROWS: ReadonlyArray<{
  name: string;
  meaning: string;
  scope: string;
}> = [
  { name: 'event_title', meaning: 'イベントのタイトル', scope: 'イベント連動の通知' },
  {
    name: 'starts_at',
    meaning: '開催日時（日本時間・YYYY/MM/DD HH:MM）',
    scope: 'イベント連動の通知',
  },
  { name: 'venue', meaning: '会場（未設定なら空）', scope: 'イベント連動の通知' },
  {
    name: 'deadline',
    meaning: '課題・アンケートの提出期限（日本時間）',
    scope: '課題・アンケートに紐づく通知',
  },
  { name: 'accept_url', meaning: '招待の受諾リンク', scope: '固定: 会員招待メール' },
  {
    name: 'expires_in_days',
    meaning: '招待リンクの有効日数',
    scope: '固定: 会員招待メール',
  },
  { name: 'display_name', meaning: '宛先の表示名', scope: '固定: パスワード再設定' },
  { name: 'reset_url', meaning: 'パスワード再設定のリンク', scope: '固定: パスワード再設定' },
  {
    name: 'expires_minutes',
    meaning: '再設定リンクの有効分数',
    scope: '固定: パスワード再設定',
  },
];

export interface OffsetParts {
  direction: 'before' | 'after';
  days: number;
  hours: number;
  minutes: number;
}

/** `-P14D` 等のISO8601期間を、フォームで編集しやすい部品（前後・日・時・分）に分解する */
export function parseOffset(offset: string | undefined | null): OffsetParts {
  if (!offset || offset === 'PT0S') return { direction: 'after', days: 0, hours: 0, minutes: 0 };
  const match = /^(-)?P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/.exec(offset);
  if (!match) return { direction: 'after', days: 0, hours: 0, minutes: 0 };
  const [, sign, days, hours, minutes] = match;
  return {
    direction: sign ? 'before' : 'after',
    days: days ? Number(days) : 0,
    hours: hours ? Number(hours) : 0,
    minutes: minutes ? Number(minutes) : 0,
  };
}

/** フォームの部品（前後・日・時・分）から ISO8601期間の文字列を組み立てる */
export function buildOffset(parts: OffsetParts): string {
  const { direction, days, hours, minutes } = parts;
  if (!days && !hours && !minutes) return 'PT0S';
  const datePart = days ? `${days}D` : '';
  const timePart =
    hours || minutes ? `T${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}` : '';
  return `${direction === 'before' ? '-' : ''}P${datePart}${timePart}`;
}
